import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to get token from OIDC localStorage
const getTokenFromStorage = (): string | null => {
  try {
    const keys = Object.keys(localStorage);
    const oidcKey = keys.find(key => key.startsWith('oidc.user:'));
    if (oidcKey) {
      const userData = JSON.parse(localStorage.getItem(oidcKey) || '{}');
      return userData.access_token || null;
    }
  } catch (e) {
    // Silently fail - token will be set via setAuthToken fallback
  }
  return null;
};

// Request interceptor - adds token to every request dynamically
apiClient.interceptors.request.use(
  (config) => {
    const token = getTokenFromStorage();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Function to set the auth token for all requests (kept for compatibility)
export const setAuthToken = (token: string | null) => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

export interface PDFField {
  name: string;
  type: string;
  value?: string;
  options?: string[];
  required: boolean;
  page_number: number;
}

export interface PDFTemplate {
  id: string;
  name: string;
  file_path: string;
  fields: PDFField[];
  created_at: string;
}

export interface DataSource {
  id: string;
  name: string;
  type: 'json' | 'csv' | 'excel' | 'database' | 'api';
  config: Record<string, any>;
  is_template: boolean;
  created_at: string;
}

export interface DataSourceField {
  name: string;
  type?: string;
}

export interface MappingDataSource {
  id: string;
  type: 'master' | 'detail';
  role: 'primary' | 'secondary';
}

export interface DataSourceRelationship {
  master_source_id: string;
  detail_source_id: string;
  master_field: string;
  detail_field: string;
}

export interface Mapping {
  id: string;
  name: string;
  description?: string;
  pdf_template_id: string;
  data_sources?: MappingDataSource[] | null;
  relationships?: DataSourceRelationship[] | null;
  field_mappings: Record<string, Record<string, string>>; // source_id -> data_field -> pdf_field
  filename_pattern?: string; // Pattern for output filename
  is_draft?: boolean; // If true, mapping is incomplete
  current_step?: number; // Wizard step (1-5) for draft resumption
  created_at: string;
  updated_at?: string;
}

export interface GeneratedPDF {
  id: string;
  mapping_id: string;
  file_name: string;
  file_path: string;
  data_row: number;
  created_at: string;
}

export interface GenerateRequest {
  mapping_id: string;
  data?: Record<string, any>;
  flatten: boolean;
}

export interface BatchGenerateRequest {
  mapping_id: string;
  flatten: boolean;
  delete_data_sources?: boolean;
}

// PDF Template API
export const uploadPDF = async (file: File): Promise<PDFTemplate> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post('/pdf/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

export const getPDFFields = async (id: string): Promise<{ id: string; name: string; fields: PDFField[] }> => {
  const response = await apiClient.get(`/pdf/${id}/fields`);
  return response.data;
};

export const listPDFTemplates = async (): Promise<PDFTemplate[]> => {
  const response = await apiClient.get('/pdf');
  return response.data || [];
};

export const getPDFTemplate = async (id: string): Promise<PDFTemplate> => {
  const response = await apiClient.get(`/pdf/${id}`);
  return response.data;
};

export const deletePDFTemplate = async (id: string): Promise<void> => {
  await apiClient.delete(`/pdf/${id}`);
};

export const downloadPDFPreview = async (id: string, fileName: string): Promise<void> => {
  const response = await apiClient.get(`/pdf/${id}/preview`, {
    responseType: 'blob',
  });

  // Create blob link to download
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `preview_${fileName}`);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Data Source API
export const uploadDataFile = async (file: File): Promise<{ file_path: string; filename: string }> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post('/datasource/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

export const createDataSource = async (dataSource: Omit<DataSource, 'id' | 'created_at'>): Promise<DataSource> => {
  const response = await apiClient.post('/datasource', dataSource);
  return response.data;
};

export const replaceDataSourceFile = async (id: string, file: File): Promise<{ file_path: string; filename: string; message: string }> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.put(`/datasource/${id}/file`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

export interface DataSourceDataResponse {
  data: Record<string, any>;
  column_order: string[];
}

export interface DataSourceBatchDataResponse {
  data: Record<string, any>[];
  column_order: string[];
}

export const getDataSourceData = async (id: string): Promise<DataSourceDataResponse> => {
  const response = await apiClient.get(`/datasource/${id}/data`);
  return response.data;
};

export const getDataSourceBatchData = async (id: string): Promise<DataSourceBatchDataResponse> => {
  const response = await apiClient.get(`/datasource/${id}/data?batch=true`);
  return response.data;
};

export const listDataSources = async (): Promise<DataSource[]> => {
  const response = await apiClient.get('/datasource');
  return response.data || [];
};

export const getDataSource = async (id: string): Promise<DataSource> => {
  const response = await apiClient.get(`/datasource/${id}`);
  return response.data;
};

export const updateDataSource = async (id: string, dataSource: Partial<DataSource>): Promise<DataSource> => {
  const response = await apiClient.put(`/datasource/${id}`, dataSource);
  return response.data;
};

export const deleteDataSource = async (id: string): Promise<void> => {
  await apiClient.delete(`/datasource/${id}`);
};

export const getExcelSheets = async (filePath: string): Promise<string[]> => {
  const response = await apiClient.get(`/datasource/sheets?file_path=${encodeURIComponent(filePath)}`);
  return response.data.sheets;
};

// Mapping API
export const createMapping = async (mapping: Omit<Mapping, 'id' | 'created_at'>): Promise<Mapping> => {
  const response = await apiClient.post('/mapping', mapping);
  return response.data;
};

export const listMappings = async (): Promise<Mapping[]> => {
  const response = await apiClient.get('/mapping');
  return response.data || [];
};

export const getMapping = async (id: string): Promise<Mapping> => {
  const response = await apiClient.get(`/mapping/${id}`);
  return response.data;
};

export const updateMapping = async (id: string, mapping: Partial<Mapping>): Promise<Mapping> => {
  const response = await apiClient.put(`/mapping/${id}`, mapping);
  return response.data;
};

export const deleteMapping = async (id: string): Promise<void> => {
  await apiClient.delete(`/mapping/${id}`);
};

// Generation API
export const generatePDF = async (request: GenerateRequest): Promise<Blob> => {
  const response = await apiClient.post('/generate', request, {
    responseType: 'blob',
  });
  return response.data;
};

export const batchGenerate = async (request: BatchGenerateRequest): Promise<{ message: string; pdfs: GeneratedPDF[]; count: number; deleted_data_sources?: number }> => {
  const response = await apiClient.post('/batch', request);
  return response.data;
};

// Generated PDFs API
export const getGeneratedPDFs = async (mappingId: string): Promise<GeneratedPDF[]> => {
  const response = await apiClient.get(`/generated?mapping=${mappingId}`);
  return response.data;
};

export const downloadGeneratedPDF = async (pdfId: string, fileName: string): Promise<void> => {
  const response = await apiClient.get(`/generated/${pdfId}/download`, {
    responseType: 'blob',
  });

  // Create blob link to download
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const deleteGeneratedPDF = async (pdfId: string): Promise<void> => {
  await apiClient.delete(`/generated/${pdfId}`);
};

export const deleteAllGeneratedPDFs = async (mappingId: string): Promise<{ message: string; count: number }> => {
  const response = await apiClient.delete(`/generated?mapping=${mappingId}`);
  return response.data;
};

export const downloadAllGeneratedPDFs = async (mappingId: string, mappingName: string): Promise<void> => {
  const response = await apiClient.get(`/generated/download-all?mapping=${mappingId}`, {
    responseType: 'blob',
  });

  // Create blob link to download
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${mappingName}.zip`);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default apiClient;
